import React from 'react';
import { CheckCircle, ArrowSquareOut } from '@phosphor-icons/react';
import Header from '../../components/landing/Header';
import Footer from '../../components/landing/Footer';
import BrandHero, { BrandHeroCopy } from '../../components/landing/BrandHero';

const ServicePage = ({
  eyebrow,
  title,
  phrases,
  lede,
  logo,
  logoAlt,
  heroImage,
  heroAlt,
  heroTone = 'burgundy',
  heroWash,
  heroFeatures = [],
  intro,
  facilities,
  gallery,
  highlights,
  closingTitle,
  closingBody,
  ctaLabel,
  ctaHref,
}) => {
  const titlePhrases = phrases?.length ? phrases : [title];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <Header />

      <BrandHero
        image={heroImage}
        imageAlt={heroAlt}
        wash={heroWash}
        className="min-h-[560px] lg:min-h-[78vh]"
      >
        <BrandHeroCopy
          logo={logo}
          logoAlt={logoAlt || eyebrow}
          eyebrow={eyebrow}
          phrases={titlePhrases}
          subtitle={lede}
          features={heroFeatures}
          tone={heroTone}
          actions={
            ctaLabel && ctaHref ? (
              <a
                href={ctaHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-[#1b4d3e] px-6 text-sm font-semibold text-white transition-colors hover:bg-[#163f33]"
              >
                {ctaLabel}
                <ArrowSquareOut size={16} weight="bold" />
              </a>
            ) : null
          }
        />
      </BrandHero>

      {/* Intro */}
      {intro && (
        <section className="bg-white pt-14 pb-4 sm:pt-20">
          <div className="mx-auto max-w-3xl px-4 text-center">
            <p className="landing-intro-lede">{intro}</p>
          </div>
        </section>
      )}

      {/* Facilities grid */}
      <section className="bg-white pt-10 pb-16 sm:pt-12 sm:pb-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="landing-section-header">
            <span className="landing-eyebrow !bg-transparent !border-primary/30 !rounded-md">
              Facilities
            </span>
            <h2 className="landing-section-title">What you can expect</h2>
            <p className="landing-section-lede mx-auto">
              Thoughtful spaces, trained people, and a calm routine — all built around your pet.
            </p>
          </div>

          <div className="landing-bento-surface grid grid-cols-1 overflow-hidden rounded-2xl border border-neutral-200 bg-white sm:grid-cols-2 lg:grid-cols-3">
            {facilities.map((tile, idx) => {
              const Icon = tile.icon;
              const col = idx % 3;
              const row = Math.floor(idx / 3);
              return (
                <div
                  key={tile.label}
                  className={[
                    'p-5 sm:p-6',
                    col !== 0 ? 'sm:border-l sm:border-neutral-200' : '',
                    row > 0 ? 'sm:border-t sm:border-neutral-200' : '',
                  ].join(' ')}
                >
                  <Icon size={22} weight="duotone" className="mb-3 text-primary" />
                  <p className="landing-card-label">{tile.label}</p>
                  <p className="landing-card-meta mt-1">{tile.sub}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="bg-neutral-100 py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="landing-section-header">
            <span className="landing-eyebrow !bg-transparent !border-primary/30 !rounded-md">
              Inside our space
            </span>
            <h2 className="landing-section-title">A glimpse around</h2>
            <p className="landing-section-lede mx-auto">
              Bright, clean, calm — designed so pets feel at ease the moment they walk in.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {gallery.map((img) => (
              <figure
                key={img.url}
                className="group relative aspect-[4/5] overflow-hidden rounded-2xl bg-neutral-200"
              >
                <img
                  src={img.url}
                  alt={img.title}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
                <figcaption className="absolute inset-x-0 bottom-0 p-4 text-white">
                  <p className="text-sm font-semibold sm:text-base">{img.title}</p>
                  <p className="mt-1 text-[11px] font-light text-white/85 sm:text-xs">
                    {img.description}
                  </p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Highlights */}
      {highlights && highlights.length > 0 && (
        <section className="bg-white py-16 sm:py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="landing-section-header">
              <span className="landing-eyebrow !bg-transparent !border-primary/30 !rounded-md">
                Highlights
              </span>
              <h2 className="landing-section-title">Small details that matter</h2>
            </div>

            <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {highlights.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 rounded-xl border border-neutral-200 bg-white p-5 text-sm font-medium text-neutral-700"
                >
                  <CheckCircle size={20} weight="fill" className="mt-0.5 flex-none text-primary" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Closing */}
      <section className="bg-neutral-100 py-16 sm:py-20">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <h2 className="landing-section-title">{closingTitle}</h2>
          <p className="landing-section-lede mx-auto mt-3">{closingBody}</p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicePage;
